import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import {
    Box, Typography, Container, Paper,
    CircularProgress, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Grid, Card, CardContent,
    Chip, Button, Avatar, Fade
} from '@mui/material';
import {
    FaTrophy, FaCalendarAlt, FaHandshake,
    FaMapMarkerAlt, FaClock, FaCalendarDay, FaSignInAlt,
    FaEnvelope, FaImage, FaUtensils,
    FaHamburger, FaBeer, FaCoffee
} from 'react-icons/fa';
import dayjs from 'dayjs';

const GruempelTurnierLive = () => {
    const { id, year } = useParams();
    const [turnier, setTurnier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const fetchTurnierData = useCallback(() => {
        const url = year
            ? `${API_BASE_URL}/api/gruemp/public/year/${year}`
            : `${API_BASE_URL}/api/gruemp/public/${id}`;
        axios.get(url)
            .then(res => {
                setTurnier(res.data);
                setError(null);
            })
            .catch(err => {
                console.error("Error fetching public tournament data", err);
                setError(year
                    ? `Für das Jahr ${year} konnte kein veröffentlichtes Grümpelturnier gefunden werden.`
                    : "Dieses Turnier konnte nicht geladen werden oder ist noch nicht veröffentlicht.");
            })
            .finally(() => setLoading(false));
    }, [id, year]);

    useEffect(() => {
        fetchTurnierData();
        // Auto-refresh every 30 seconds for live results
        const interval = setInterval(fetchTurnierData, 30000);
        return () => clearInterval(interval);
    }, [fetchTurnierData]);


    if (loading) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" gap={2}>
                <CircularProgress color="primary" size={50} />
                <Typography variant="body1" color="textSecondary">Lade Turnierdaten...</Typography>
            </Box>
        );
    }

    if (error || !turnier) {
        return (
            <Container maxWidth="md" sx={{ mt: 6 }}>
                <Alert severity="error" variant="filled" sx={{ borderRadius: 3 }}>{error || "Grümpelturnier wurde nicht gefunden."}</Alert>
                <Box mt={4} textAlign="center">
                    <Button variant="outlined" component={Link} to="/login" startIcon={<FaSignInAlt />} sx={{ borderRadius: 3 }}>
                        Zum Login
                    </Button>
                </Box>
            </Container>
        );
    }

    // Dynamic calculations for standings
    const calculateStandings = (groupName) => {
        const groupObj = turnier.groups?.find(g => g.name === groupName);
        if (!groupObj) return [];

        const teamStats = {};
        groupObj.teams.forEach(team => {
            teamStats[team] = {
                team,
                played: 0,
                won: 0,
                drawn: 0,
                lost: 0,
                goalsFor: 0,
                goalsAgainst: 0,
                points: 0
            };
        });

        const groupMatches = turnier.matches?.filter(m =>
            m.group === groupName && m.status === 'Completed' &&
            m.scoreA !== null && m.scoreB !== null
        ) || [];

        groupMatches.forEach(m => {
            const statsA = teamStats[m.teamA];
            const statsB = teamStats[m.teamB];

            if (statsA && statsB) {
                statsA.played += 1;
                statsB.played += 1;
                statsA.goalsFor += m.scoreA;
                statsA.goalsAgainst += m.scoreB;
                statsB.goalsFor += m.scoreB;
                statsB.goalsAgainst += m.scoreA;

                if (m.scoreA > m.scoreB) {
                    statsA.won += 1;
                    statsA.points += 3;
                    statsB.lost += 1;
                } else if (m.scoreA < m.scoreB) {
                    statsB.won += 1;
                    statsB.points += 3;
                    statsA.lost += 1;
                } else {
                    statsA.drawn += 1;
                    statsA.points += 1;
                    statsB.drawn += 1;
                    statsB.points += 1;
                }
            }
        });

        return Object.values(teamStats).sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points;
            const diffA = a.goalsFor - a.goalsAgainst;
            const diffB = b.goalsFor - b.goalsAgainst;
            if (diffB !== diffA) return diffB - diffA;
            if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
            return a.team.localeCompare(b.team);
        });
    };

    const resolveTeamName = (teamName) => {
        if (!teamName) return '';
        const matchGroup = teamName.match(/^(\d+)\.\s*Gruppe\s+(.+)$/i);
        if (matchGroup) {
            const position = parseInt(matchGroup[1]) - 1;
            const groupName = matchGroup[2].trim();
            const standings = calculateStandings(groupName);
            if (standings && standings[position]) {
                return `${standings[position].team} (${teamName})`;
            }
        }
        return teamName;
    };

    const shareUrl = window.location.href;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(shareUrl)}`;

    // Tab categories
    const tabs = [
        { label: "Startseite", icon: <FaCalendarDay /> },
        { label: "Spielplan", icon: <FaCalendarAlt /> },
        { label: "Tabellen", icon: <FaTrophy /> },
        { label: "Verpflegung", icon: <FaUtensils /> },
        { label: "Sponsoren", icon: <FaHandshake /> },
        { label: "Galerie", icon: <FaImage />, hide: !turnier.images || turnier.images.length === 0 }
    ].filter(t => !t.hide);

    return (
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, md: 4 } }}>

            {/* Premium Header Banner */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    background: 'linear-gradient(135deg, #1e1e2f 0%, #d32f2f 100%)',
                    color: 'white',
                    borderRadius: '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 12px 40px rgba(211, 47, 47, 0.15)',
                    textAlign: { xs: 'center', md: 'left' }
                }}
            >
                {/* Decorative Sport Graphics */}
                <Box
                    sx={{
                        position: 'absolute',
                        right: -50,
                        top: -50,
                        width: 250,
                        height: 250,
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '50%',
                        pointerEvents: 'none'
                    }}
                />

                <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h3" component="h1" fontWeight="800" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3.2rem' }, textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                            {turnier.name}
                        </Typography>

                        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'center', md: 'flex-start' }} gap={2} sx={{ mt: 2 }}>
                            <Chip
                                icon={<FaCalendarDay style={{ color: 'white' }} />}
                                label={dayjs(turnier.date).format('DD. MMMM YYYY')}
                                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: '600', backdropFilter: 'blur(5px)' }}
                            />
                            {turnier.location && (
                                <Chip
                                    icon={<FaMapMarkerAlt style={{ color: 'white' }} />}
                                    label={turnier.location}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: '600', backdropFilter: 'blur(5px)' }}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Quick Action Navigation Buttons (Mobile-first, no scrolling needed) */}
            <Grid container spacing={1.5} sx={{ mb: 4 }}>
                {tabs.map((tab, idx) => {
                    const isActive = activeTab === idx;
                    return (
                        <Grid item xs={6} sm={4} md={12 / Math.min(tabs.length, 6)} key={idx}>
                            <Button
                                fullWidth
                                onClick={() => setActiveTab(idx)}
                                startIcon={tab.icon}
                                sx={{
                                    py: 1.3,
                                    px: 1.5,
                                    borderRadius: '14px',
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                                    boxShadow: isActive ? '0 6px 16px rgba(211, 47, 47, 0.25)' : 'none',
                                    border: '1px solid',
                                    borderColor: isActive ? 'primary.main' : 'rgba(0,0,0,0.08)',
                                    bgcolor: isActive ? 'primary.main' : '#ffffff',
                                    color: isActive ? '#ffffff' : 'text.primary',
                                    textTransform: 'none',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: isActive ? 'primary.dark' : '#f8f8f8',
                                        borderColor: isActive ? 'primary.dark' : 'rgba(0,0,0,0.15)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                {tab.label}
                            </Button>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Tab Panes */}
            <Box>
                {/* TAB 0: STARTSEITE */}
                {activeTab === 0 && (
                    <Fade in timeout={400}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                {/* Live / Next Matches Preview */}
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }} color="textPrimary">
                                    Aktuelle & Kommende Spiele
                                </Typography>
                                {(!turnier.matches || turnier.matches.length === 0) ? (
                                    <Alert severity="info" sx={{ borderRadius: '16px' }}>Der Spielplan wird in Kürze freigeschaltet.</Alert>
                                ) : (
                                    <Box display="flex" flexDirection="column" gap={3}>
                                        {(() => {
                                            const uncompleted = turnier.matches.filter(m => m.status !== 'Completed');
                                            if (uncompleted.length === 0) {
                                                return (
                                                    <Alert severity="success" sx={{ borderRadius: '16px' }}>
                                                        Alle Spiele wurden erfolgreich beendet! Schau in die Tabellen für den Endstand.
                                                    </Alert>
                                                );
                                            }

                                            // Sammle alle eindeutigen Zeiten der verbleibenden Spiele
                                            const timeSlots = Array.from(new Set(uncompleted.map(m => m.time)));
                                            const currentSlotTime = timeSlots[0];
                                            const nextSlotTime = timeSlots[1];

                                            const currentMatches = uncompleted.filter(m => m.time === currentSlotTime);
                                            const nextMatches = nextSlotTime ? uncompleted.filter(m => m.time === nextSlotTime) : [];

                                            return (
                                                <>
                                                    {/* Aktuelle Runde */}
                                                    <Box>
                                                        <Box mb={1.5}>
                                                            <Paper
                                                                elevation={0}
                                                                sx={{
                                                                    display: 'flex',
                                                                    width: { xs: '100%', sm: 'auto' },
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    px: 2,
                                                                    py: 1,
                                                                    borderRadius: '14px',
                                                                    bgcolor: '#ffffff',
                                                                    border: '1px solid #eee',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                                                }}
                                                            >
                                                                <Chip label="JETZT" size="small" sx={{ fontWeight: 'bold', bgcolor: '#d32f2f', color: '#ffffff', px: 0.5 }} />
                                                                <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ fontSize: '1.05rem' }}>
                                                                    {currentSlotTime} Uhr
                                                                </Typography>
                                                            </Paper>
                                                        </Box>
                                                        <Grid container spacing={1.5}>
                                                            {currentMatches.map((match) => (
                                                                <Grid item xs={12} sm={6} key={match._id || `${match.time}-${match.court}`}>
                                                                    <Card sx={{ borderRadius: '16px', borderLeft: '6px solid #d32f2f', bgcolor: '#ffffff', border: '1px solid #eee', boxShadow: '0 4px 14px rgba(211,47,47,0.08)' }}>
                                                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                                                <Chip label={match.court || 'Platz 1'} size="small" variant="outlined" color="primary" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.75rem' }} />
                                                                                <Typography variant="caption" color="textSecondary" fontWeight="500">
                                                                                    {match.round} {match.group ? `• ${match.group}` : ''}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="body1" fontWeight="bold" color="textPrimary" sx={{ mt: 0.5 }}>
                                                                                {resolveTeamName(match.teamA)} <Typography component="span" color="error.main" fontWeight="bold">vs.</Typography> {resolveTeamName(match.teamB)}
                                                                            </Typography>
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>

                                                    {/* Nächste Runde */}
                                                    {nextMatches.length > 0 && (
                                                        <Box mt={2.5}>
                                                            <Box mb={1.5}>
                                                                <Paper
                                                                    elevation={0}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        width: { xs: '100%', sm: 'auto' },
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        px: 2,
                                                                        py: 1,
                                                                        borderRadius: '14px',
                                                                        bgcolor: '#ffffff',
                                                                        border: '1px solid #eee',
                                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                                                    }}
                                                                >
                                                                    <Chip label="DEMNÄCHST" size="small" sx={{ fontWeight: 'bold', bgcolor: '#ed6c02', color: '#ffffff', px: 0.5 }} />
                                                                    <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ fontSize: '1.05rem' }}>
                                                                        {nextSlotTime} Uhr
                                                                    </Typography>
                                                                </Paper>
                                                            </Box>
                                                            <Grid container spacing={1.5}>
                                                                {nextMatches.map((match) => (
                                                                    <Grid item xs={12} sm={6} key={match._id || `${match.time}-${match.court}`}>
                                                                        <Card sx={{ borderRadius: '16px', borderLeft: '6px solid #ed6c02', bgcolor: '#ffffff', border: '1px solid #eee', boxShadow: '0 4px 14px rgba(237,108,2,0.08)' }}>
                                                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                                                    <Chip label={match.court || 'Platz 1'} size="small" variant="outlined" color="warning" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.75rem' }} />
                                                                                    <Typography variant="caption" color="textSecondary" fontWeight="500">
                                                                                        {match.round} {match.group ? `• ${match.group}` : ''}
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Typography variant="body1" fontWeight="bold" color="textPrimary" sx={{ mt: 0.5 }}>
                                                                                    {resolveTeamName(match.teamA)} <Typography component="span" color="warning.main" fontWeight="bold">vs.</Typography> {resolveTeamName(match.teamB)}
                                                                                </Typography>
                                                                            </CardContent>
                                                                        </Card>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Card sx={{ borderRadius: '20px', bgcolor: 'white', border: '1px solid #eee', mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <FaEnvelope size={36} color="#d32f2f" style={{ marginBottom: '10px' }} />
                                        <Typography variant="h6" fontWeight="bold" gutterBottom>Fragen?</Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            Melde dich bei Anmerkungen oder Fragen direkt beim Orgateam:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold" color="primary">{turnier.contact || "Orgateam"}</Typography>
                                    </CardContent>
                                </Card>

                                <Card sx={{ borderRadius: '20px', p: 1 }}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Mobil dabei sein</Typography>
                                        <Box component="img" src={qrCodeUrl} sx={{ width: '130px', height: '130px', my: 1, border: '1px solid #ddd', p: 1, borderRadius: 2 }} />
                                        <Typography variant="caption" color="textSecondary" display="block">Code scannen für Live-Ergebnisse auf dem Handy</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Sponsoren-Leiste auf der Startseite */}
                            {turnier.sponsors && turnier.sponsors.length > 0 && (
                                <Grid item xs={12} mt={1}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: '20px', border: '1px solid #eee', bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                        <Typography variant="subtitle2" fontWeight="bold" align="center" color="textSecondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.8rem' }}>
                                            Unsere Sponsoren
                                        </Typography>
                                        <Grid container spacing={2} justifyContent="center" alignItems="center">
                                            {turnier.sponsors.map((sponsor) => (
                                                <Grid item xs={6} sm={4} md={2} key={sponsor._id} textAlign="center">
                                                    <Box
                                                        component={sponsor.website ? "a" : "div"}
                                                        href={sponsor.website || undefined}
                                                        target={sponsor.website ? "_blank" : undefined}
                                                        rel={sponsor.website ? "noopener noreferrer" : undefined}
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            p: 1,
                                                            borderRadius: '12px',
                                                            textDecoration: 'none',
                                                            transition: 'transform 0.2s',
                                                            '&:hover': sponsor.website ? { transform: 'scale(1.05)' } : {}
                                                        }}
                                                    >
                                                        {sponsor.logoUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={`${API_BASE_URL}${sponsor.logoUrl}`}
                                                                alt={sponsor.name}
                                                                sx={{ maxHeight: 50, maxWidth: '100%', objectFit: 'contain' }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" fontWeight="bold" color="textPrimary">
                                                                {sponsor.name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </Fade>
                )}

                {/* TAB 1: SPIELPLAN */}
                {activeTab === tabs.findIndex(t => t.label === "Spielplan") && (
                    <Fade in>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
                                Spielplan & Ergebnisse
                            </Typography>

                            {!turnier.matches || turnier.matches.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '16px' }}>Der Spielplan wurde noch nicht veröffentlicht.</Alert>
                            ) : (
                                turnier.matches
                                    .sort((a, b) => a.time.localeCompare(b.time))
                                    .map((match) => (
                                        <Card
                                            key={match._id}
                                            sx={{
                                                borderRadius: '16px',
                                                borderLeft: match.status === 'Completed' ? '6px solid #4caf50' : '6px solid #ed6c02',
                                                transition: 'all 0.2s',
                                                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }
                                            }}
                                        >
                                            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                                <Grid container alignItems="center" spacing={2}>
                                                    <Grid item xs={12} sm={3}>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                                                                <FaClock size={16} />
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="h6" fontWeight="bold">{match.time} Uhr</Typography>
                                                                <Typography variant="caption" color="textSecondary">{match.court || 'Court 1'}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={8} sm={6}>
                                                        <Box display="flex" flexDirection="column" gap={1}>
                                                            <Typography
                                                                variant="body1"
                                                                fontWeight={match.status === 'Completed' && match.scoreA > match.scoreB ? '700' : '500'}
                                                                color={match.status === 'Completed' && match.scoreA < match.scoreB ? 'text.secondary' : 'text.primary'}
                                                            >
                                                                {resolveTeamName(match.teamA)}
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                fontWeight={match.status === 'Completed' && match.scoreB > match.scoreA ? '700' : '500'}
                                                                color={match.status === 'Completed' && match.scoreA > match.scoreB ? 'text.secondary' : 'text.primary'}
                                                            >
                                                                {resolveTeamName(match.teamB)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={4} sm={3}>
                                                        <Box
                                                            display="flex"
                                                            flexDirection="column"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            bgcolor={match.status === 'Completed' ? 'rgba(76,175,80,0.1)' : '#f9f9f9'}
                                                            border={match.status === 'Completed' ? '1px solid rgba(76,175,80,0.2)' : '1px solid #eee'}
                                                            borderRadius="12px"
                                                            py={1.5}
                                                        >
                                                            {match.status === 'Completed' && match.scoreA !== null && match.scoreB !== null ? (
                                                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                                                    {match.scoreA} : {match.scoreB}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="h6" fontWeight="bold" color="text.secondary">
                                                                    - : -
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))
                            )}
                        </Box>
                    </Fade>
                )}

                {/* TAB 2: TABELLEN */}
                {activeTab === tabs.findIndex(t => t.label === "Tabellen") && (
                    <Fade in>
                        <Box display="flex" flexDirection="column" gap={4}>
                            <Typography variant="h5" fontWeight="bold" color="primary">
                                Gruppentabellen
                            </Typography>

                            {!turnier.groups || turnier.groups.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '16px' }}>Es sind noch keine Gruppen eingeteilt.</Alert>
                            ) : (
                                turnier.groups.map((group) => {
                                    const standings = calculateStandings(group.name);
                                    return (
                                        <Paper key={group.name} elevation={0} sx={{ p: 3, borderRadius: '20px', border: '1px solid #eee' }}>
                                            <Typography variant="h5" fontWeight="bold" mb={2} color="secondary.main">
                                                {group.name}
                                            </Typography>

                                            <TableContainer>
                                                <Table size="medium">
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                            <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Pl.</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Mannschaft</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Sp.</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>S</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>U</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>N</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>Tore</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Pkt.</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {standings.map((row, idx) => (
                                                            <TableRow key={row.team} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>{idx + 1}</TableCell>
                                                                <TableCell sx={{ fontWeight: '600' }}>{row.team}</TableCell>
                                                                <TableCell align="center">{row.played}</TableCell>
                                                                <TableCell align="center">{row.won}</TableCell>
                                                                <TableCell align="center">{row.drawn}</TableCell>
                                                                <TableCell align="center">{row.lost}</TableCell>
                                                                <TableCell align="center">{row.goalsFor}:{row.goalsAgainst}</TableCell>
                                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.05rem' }}>
                                                                    {row.points}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Paper>
                                    );
                                })
                            )}
                        </Box>
                    </Fade>
                )}



                {/* TAB: VERPFLEGUNG */}
                {activeTab === tabs.findIndex(t => t.label === "Verpflegung") && (
                    <Fade in>
                        <Box>
                            <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
                                Speisen & Getränke
                            </Typography>
                            <Typography variant="body1" color="textSecondary" mb={3}>
                                Unser Angebot direkt am Turniertag. Bons gibt es an der Kasse im Clubheim.
                            </Typography>

                            {!turnier.catering || turnier.catering.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '16px' }}>
                                    Es wurden noch keine Speisen oder Getränke hinterlegt.
                                </Alert>
                            ) : (
                                (() => {
                                    const categories = [...new Set(turnier.catering.map(item => item.category))];
                                    return categories.map(cat => {
                                        const items = turnier.catering.filter(i => i.category === cat);
                                        const catIcon = cat === 'Getränke' ? <FaBeer size={18} /> :
                                            cat === 'Kaffee & Kuchen' ? <FaCoffee size={18} /> :
                                                <FaHamburger size={18} />;
                                        return (
                                            <Box key={cat} mb={4}>
                                                <Box display="flex" alignItems="center" gap={1.5} mb={2}
                                                    sx={{
                                                        bgcolor: '#ffffff',
                                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                        borderRadius: '12px',
                                                        px: 2.5,
                                                        py: 1.2,
                                                        display: 'inline-flex'
                                                    }}
                                                >
                                                    <Avatar sx={{ bgcolor: 'rgba(211,47,47,0.10)', color: 'primary.main', width: 38, height: 38 }}>
                                                        {catIcon}
                                                    </Avatar>
                                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                                        {cat}
                                                    </Typography>
                                                </Box>
                                                <Paper elevation={0} variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                                                    {[...items].sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, idx) => (
                                                        <Box
                                                            key={item._id || idx}
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            px={3}
                                                            py={2}
                                                            sx={{
                                                                borderBottom: idx < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                '&:hover': { bgcolor: '#fafafa' },
                                                                transition: 'background 0.2s'
                                                            }}
                                                        >
                                                            <Box>
                                                                <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
                                                                    <Typography variant="body1" fontWeight="600">
                                                                        {item.name}
                                                                    </Typography>
                                                                    {item.size && (
                                                                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                                            {item.size}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                {item.description && (
                                                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                                                        {item.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Chip
                                                                label={item.price || '-'}
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 'bold', minWidth: 72, justifyContent: 'center' }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Paper>
                                            </Box>
                                        );
                                    });
                                })()
                            )}
                        </Box>
                    </Fade>
                )}

                {/* TAB: SPONSOREN */}
                {activeTab === tabs.findIndex(t => t.label === "Sponsoren") && (
                    <Fade in>
                        <Box>
                            <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
                                Unsere Sponsoren
                            </Typography>
                            <Typography variant="body1" color="textSecondary" mb={3}>
                                Ein herzliches Dankeschön an alle Sponsoren, die dieses Event unterstützen!
                            </Typography>

                            {!turnier.sponsors || turnier.sponsors.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '16px' }}>
                                    Es wurden noch keine Sponsoren hinterlegt.
                                </Alert>
                            ) : (
                                <Grid container spacing={3}>
                                    {turnier.sponsors.map((sponsor) => (
                                        <Grid item xs={6} sm={4} md={3} key={sponsor._id}>
                                            <Card
                                                component={sponsor.website ? "a" : "div"}
                                                href={sponsor.website || undefined}
                                                target={sponsor.website ? "_blank" : undefined}
                                                rel={sponsor.website ? "noopener noreferrer" : undefined}
                                                sx={{
                                                    p: 3,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: 160,
                                                    borderRadius: '20px',
                                                    textDecoration: 'none',
                                                    border: '1px solid #eee',
                                                    boxShadow: 'none',
                                                    transition: 'all 0.3s',
                                                    cursor: sponsor.website ? 'pointer' : 'default',
                                                    '&:hover': sponsor.website ? {
                                                        boxShadow: '0px 10px 30px rgba(0,0,0,0.08)',
                                                        transform: 'translateY(-5px)',
                                                        borderColor: 'primary.main'
                                                    } : {}
                                                }}
                                            >
                                                {sponsor.logoUrl ? (
                                                    <Box
                                                        component="img"
                                                        src={`${API_BASE_URL}${sponsor.logoUrl}`}
                                                        alt={sponsor.name}
                                                        sx={{
                                                            maxWidth: '100%',
                                                            maxHeight: 90,
                                                            objectFit: 'contain',
                                                            mb: 1.5
                                                        }}
                                                    />
                                                ) : (
                                                    <Avatar sx={{ width: 60, height: 60, mb: 1.5, bgcolor: 'primary.light' }}>
                                                        <FaHandshake size={30} />
                                                    </Avatar>
                                                )}
                                                <Typography variant="subtitle1" align="center" fontWeight="bold" color="textPrimary">
                                                    {sponsor.name}
                                                </Typography>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Fade>
                )}

                {/* TAB: GALERIE */}
                {activeTab === tabs.findIndex(t => t.label === "Galerie") && (
                    <Fade in>
                        <Box>
                            <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
                                Turnier-Galerie
                            </Typography>
                            <Typography variant="body1" color="textSecondary" mb={4}>
                                Die besten Impressionen direkt vom Platz.
                            </Typography>

                            {!turnier.images || turnier.images.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '16px' }}>
                                    Es wurden noch keine Bilder hochgeladen.
                                </Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    {turnier.images.map((img, idx) => (
                                        <Grid item xs={12} sm={6} md={4} key={img._id || idx}>
                                            <Card sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #eee', boxShadow: 'none', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } }}>
                                                <Box
                                                    component="img"
                                                    src={`${API_BASE_URL}${img.url || img}`}
                                                    alt={img.caption || `Bild ${idx + 1}`}
                                                    sx={{ width: '100%', height: 240, objectFit: 'cover', display: 'block' }}
                                                />
                                                {img.caption && (
                                                    <Box px={2} py={1}>
                                                        <Typography variant="caption" color="textSecondary">{img.caption}</Typography>
                                                    </Box>
                                                )}
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Fade>
                )}



            </Box>
        </Container>
    );
};

export default GruempelTurnierLive;
